import { useState } from "react";
import { DEFAULT_SLICE, createInitialSlices, detectPattern } from "./rewardWheel.constants";

/**
 * Encapsulates all slice state management and duplication logic
 * shared between RewardWheelCreatePage and RewardWheelDetailPage.
 *
 * @param {number} initialCount - number of slices to initialise with
 * @param {boolean} initialDuplicate - initial duplicate toggle value
 */
const useSliceManager = (initialCount = 8, initialDuplicate = false) => {
  const [slices, setSlices] = useState(() => createInitialSlices(initialCount));
  const [displayedSlices, setDisplayedSlices] = useState(initialCount);
  const [duplicateSlice, setDuplicateSlice] = useState(initialDuplicate);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const replicatePattern = (currentSlices, pattern, count) => {
    const updated = [...currentSlices];
    for (let i = 0; i < count; i++) {
      updated[i] = { ...pattern[i % pattern.length] };
    }
    return updated;
  };

  // ─── Actions ────────────────────────────────────────────────────────────────

  /**
   * Replace slices with an external array (used when loading from the API).
   */
  const initSlices = (newSlices, count) => {
    setSlices(newSlices);
    setDisplayedSlices(count);
  };

  const handleDisplayedSlicesChange = (count) => {
    setDisplayedSlices(count);
    setSlices((prev) => {
      if (count > prev.length) {
        const added = createInitialSlices(count - prev.length);
        const newSlices = [...prev, ...added];
        if (duplicateSlice) {
          const pattern = detectPattern(prev, prev.length);
          if (pattern.length > 0) {
            for (let i = prev.length; i < count; i++) {
              newSlices[i] = { ...pattern[i % pattern.length] };
            }
          }
        }
        return newSlices;
      }
      return prev.slice(0, count);
    });
  };

  const handleDuplicateToggle = () => {
    setDuplicateSlice((prevDuplicate) => {
      const newVal = !prevDuplicate;
      if (newVal) {
        setSlices((current) => {
          const pattern = detectPattern(current, displayedSlices);
          if (pattern.length === 0) return current;
          return replicatePattern(current, pattern, displayedSlices);
        });
      } else {
        setSlices((current) => {
          const pattern = detectPattern(current, displayedSlices);
          if (pattern.length === 0) return current;
          const updated = [...current];
          for (let i = pattern.length; i < displayedSlices; i++) {
            updated[i] = { ...DEFAULT_SLICE };
          }
          return updated;
        });
      }
      return newVal;
    });
  };

  const handleSliceChange = (index, field, value) => {
    setSlices((prev) => {
      const updated = [...prev];

      if (field === "productSearch" && !value) {
        // When removing a product, also clear discount and re-arrange
        if (duplicateSlice) {
          // Get the current pattern
          const pattern = detectPattern(prev, displayedSlices);
          if (pattern.length === 0) {
            // No pattern, just clear this slot
            updated[index] = { ...DEFAULT_SLICE };
            return updated;
          }

          // Calculate which slot in the pattern is being removed
          const slot = index % pattern.length;
          
          // Remove the product at this slot from the pattern
          const newPattern = pattern.filter((_, i) => i !== slot);
          
          if (newPattern.length === 0) {
            // No products left, clear all
            return updated.map((_, i) =>
              i < displayedSlices ? { ...DEFAULT_SLICE } : updated[i],
            );
          }
          
          // Fill the array with new pattern repeated
          for (let i = 0; i < displayedSlices; i++) {
            updated[i] = { ...newPattern[i % newPattern.length] };
          }
          return updated;
        } else {
          // If duplicate is OFF, re-arrange products by shifting left
          const productsBeforeIndex = prev.slice(0, index).filter((s) => s.productId);
          const productsAfterIndex = prev.slice(index + 1, displayedSlices).filter((s) => s.productId);
          const rearrangedProducts = [...productsBeforeIndex, ...productsAfterIndex];
          
          // Fill the array with rearranged products, rest with DEFAULT_SLICE
          for (let i = 0; i < displayedSlices; i++) {
            if (i < rearrangedProducts.length) {
              updated[i] = rearrangedProducts[i];
            } else {
              updated[i] = { ...DEFAULT_SLICE };
            }
          }
          return updated;
        }
      }

      updated[index] = { ...updated[index], [field]: value };

      if (duplicateSlice) {
        const pattern = detectPattern(prev, displayedSlices);
        const patternLength = pattern.length;
        if (patternLength > 0) {
          const slot = index % patternLength;
          for (let i = slot; i < displayedSlices; i += patternLength) {
            if (i !== index) updated[i] = { ...updated[i], [field]: value };
          }
        }
      }
      return updated;
    });
  };

  const handleProductSelected = (modalSliceIndex, productData, onDone) => {
    if (modalSliceIndex === null) return;
    const isFreeProduct = Number(productData.price) === 0;
    const displayName = productData.variantName
      ? `${productData.productName} — ${productData.variantName}`
      : productData.productName;

    const productFields = {
      productSearch: displayName,
      price: productData.price,
      productId: productData.productId,
      variantId: productData.variantId,
      productName: productData.productName,
      variantName: productData.variantName,
      businessId: productData.businessId,
      ...(isFreeProduct ? { discount: "" } : {}),
    };

    setSlices((prev) => {
      const updated = [...prev];
      updated[modalSliceIndex] = {
        ...updated[modalSliceIndex],
        ...productFields,
      };

      if (duplicateSlice) {
        const pattern = detectPattern(prev, displayedSlices);
        const patternLength = pattern.length;
        if (patternLength > 0) {
          const slot = modalSliceIndex % patternLength;
          for (let i = slot; i < displayedSlices; i += patternLength) {
            if (i !== modalSliceIndex) {
              updated[i] = { ...updated[i], ...productFields };
            }
          }
        }
      }
      return updated;
    });

    if (onDone) onDone();
  };

  return {
    slices,
    displayedSlices,
    duplicateSlice,
    setDuplicateSlice,
    initSlices,
    handleDisplayedSlicesChange,
    handleDuplicateToggle,
    handleSliceChange,
    handleProductSelected,
  };
};

export default useSliceManager;
